<template>
  <div class="vue-tooltip">
    <div class="outlet">
      <slot name="outlet"></slot>
    </div>
    <div :style="tooltipStyles" :class="tooltipClass" v-if="showOnRender">
      <div class="inner">
        <slot name="tooltip"></slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Tooltip',
  data() {
    return {
      top: 0,
      left: 0,
      isShowing: false,
      hasArrow: false,
      hasRightArrow: false,
      tooltipControl: null,
    };
  },
  props: {
    placement: {
      type: String,
      default: 'top-right',
    },
    bgColor: {
      type: String,
    },
    scale: {
      type: Number,
      default: 1,
    },
    margin: {
      type: Number,
      default: 0,
    },
    arrow: {
      type: Boolean,
      default: false,
    },
    rightArrow: {
      type: Boolean,
      default: false,
    },
    customClasses: {
      type: Object,
      default: () => {
        return {};
      },
    },
    showOnRender: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    outletEl() {
      return this.$el.querySelector('.outlet');
    },
    outletInnerEl() {
      return this.$el.querySelector('.outlet > *');
    },
    tooltipEl() {
      return this.$el.querySelector('.tooltip');
    },
    tooltipClass() {
      let tooltipClasses = {
        tooltip: true,
      };

      tooltipClasses[`bg-${this.bgColor}`] = !!this.bgColor;

      return {
        ...tooltipClasses,
        ...this.customClasses,
        'tooltip--show': this.isShowing,
        'tooltip--hide': !this.isShowing,
        'tooltip--arrow': this.hasArrow,
        'tooltip--arrow--right': this.hasRightArrow,
      };
    },
    tooltipStyles() {
      return {
        top: `${this.top}px`,
        left: `${this.left}px`,
      };
    },
  },
  methods: {
    place() {
      if (this.showOnRender === false) {
        this.top = 0;
        this.left = 0;

        return;
      }

      let containerRect = this.$el.getBoundingClientRect();
      let outletInnerRect = this.outletInnerEl.getBoundingClientRect();
      let tooltipRect = this.tooltipEl.getBoundingClientRect();

      if (
        this.placement.indexOf('top') === 0 ||
        this.placement.indexOf('bottom') === 0
      ) {
        if (this.placement.indexOf('top') === 0) {
          this.top =
            (outletInnerRect.top - containerRect.top - tooltipRect.height) /
              this.scale +
            this.margin;
        } else {
          this.top =
            (outletInnerRect.bottom - containerRect.top) / this.scale +
            this.margin;
        }

        if (this.placement.indexOf('left') !== -1) {
          const left = tooltipRect.width / this.scale - this.margin;
          this.left = left > 0 ? -left : left;
        } else if (this.placement.indexOf('right') !== -1) {
          this.left =
            (outletInnerRect.left - containerRect.left + containerRect.width) /
              this.scale -
            this.margin;
        } else {
          this.left =
            (outletInnerRect.left -
              containerRect.left +
              (outletInnerRect.width - tooltipRect.width) / 2) /
            this.scale;
        }
      }
    },
    show() {
      if (this.isShowing) {
        return;
      }

      this.isShowing = true;

      this.$nextTick(() => {
        this.place();
      });
    },
    hide() {
      if (!this.isShowing) {
        return;
      }

      this.isShowing = false;
    },
    onShowTooltip(e) {
      e.preventDefault();
      this.show();
    },
    onHideTooltip(e) {
      e.preventDefault();
      this.hide();
    },
    onShowHideTooltip(e) {
      e.preventDefault();
      if (!this.isShowing) {
        this.show();
      } else {
        this.hide();
      }
    },
  },
  watch: {
    arrow: {
      handler(value) {
        this.hasArrow = value;
      },
      immediate: true,
    },
    rightArrow: {
      handler(value) {
        this.hasRightArrow = value;
      },
      immediate: true,
    },
  },
  mounted() {
    this.outletEl.addEventListener('mouseenter', this.onShowTooltip);
    this.outletEl.addEventListener('mouseleave', this.onHideTooltip);
    this.outletEl.addEventListener('touchstart', this.onShowTooltip);
    this.outletEl.addEventListener('touchend', this.onHideTooltip);

    this.tooltipControl = this.outletEl.querySelector('.tooltip-control');
    if (this.tooltipControl) {
      this.tooltipControl.addEventListener('focus', this.onShowTooltip);
      this.tooltipControl.addEventListener('blur', this.onHideTooltip);
    }

    window.addEventListener('resize', this.place);
  },
  destroyed() {
    this.outletEl.removeEventListener('mouseenter', this.onShowTooltip);
    this.outletEl.removeEventListener('mouseleave', this.onHideTooltip);
    this.outletEl.removeEventListener('touchstart', this.onShowTooltip);
    this.outletEl.removeEventListener('touchend', this.onHideTooltip);

    if (this.tooltipControl) {
      this.tooltipControl.removeEventListener('focus', this.onShowTooltip);
      this.tooltipControl.removeEventListener('blur', this.onHideTooltip);
    }

    window.removeEventListener('resize', this.place);
  },
};
</script>

<style lang="stylus" scoped>
@import "tooltip.styl";
</style>
